// Marketing home: ask Courage for what you want, then the work that's ready for you (next steps found in the records
// and already drafted), with what's going out this week and what marketing is producing beside it. The records
// themselves (calendar, campaigns, emails, leads) are one click away in the area menu.
import { Link } from "react-router-dom";
import { Chat } from "../Chat";
import { useLoad } from "../hooks";
import { mk, usd, todayIso, iso, CONTENT_STATE_LABEL, type Content } from "./mkApi";
import { em, pctOf, n } from "./emApi";
import { ReadyForYou } from "./Ready";
import { SiteHeader } from "../areaSite";
import { findArea } from "../suite";
import "./mk.css";
import "./ready.css";

const PROMPTS = [
  "Fill the webinar: who should we invite?",
  "Plan next month's LinkedIn posts from what we shipped and won",
  "Which customers stopped ordering, and what should we send them?",
  "How did our last email do, and who clicked?",
];

export function MarketingHome() {
  const s = useLoad(() => mk.summary());
  const week = useLoad(() => { const t = new Date(); return mk.content({ from: iso(t), to: iso(new Date(t.getTime() + 6 * 864e5)) }); });
  const camps = useLoad(() => mk.campaigns());
  const mail = useLoad(() => em.summary().catch(() => null));
  const reload = () => { s.reload(); week.reload(); camps.reload(); };
  return (
    <div className="page area-home area-site mk-home">
      <SiteHeader d={findArea("marketing")!} summary="Plan content, run campaigns, email the right people and capture leads that flow straight into accounts and opportunities.">
        <Chat area="marketing" compact placeholder="Tell Courage what you want, like fill the webinar" prompts={PROMPTS} onChanged={reload} />
      </SiteHeader>
      <div className="mk-desk">
        <ReadyForYou onChanged={reload} />
        <aside className="mk-side" aria-label="This week and results">
          <ThisWeek rows={week.data} late={s.data?.overdue ?? 0} />
          <Producing s={s.data} camps={camps.data} mail={mail.data} />
        </aside>
      </div>
    </div>
  );
}

function ThisWeek({ rows, late }: { rows: Content[] | null; late: number }) {
  const today = todayIso();
  const days = Array.from({ length: 7 }, (_, i) => iso(new Date(Date.now() + i * 864e5)));
  const by = new Map<string, Content[]>();
  for (const c of (rows ?? []).filter((c) => c.state !== "cancelled")) by.set(c.publishDate!, [...(by.get(c.publishDate!) ?? []), c]);
  const shown = days.filter((d) => d === today || by.has(d));
  return (
    <section className="factbox mk-week" aria-labelledby="wk-h">
      <div className="mk-side-h"><h2 id="wk-h">This week</h2><Link to="/marketing/calendar">Calendar</Link></div>
      {late > 0 && <p className="mk-late"><Link to="/marketing/calendar?view=list">{late === 1 ? "1 item is late" : `${late} items are late`}</Link></p>}
      {!rows ? <p className="empty-note">Loading…</p> : (
        <ol className="mk-days">
          {shown.map((d) => (
            <li key={d} className={d === today ? "today" : ""}>
              <span className="d">{d === today ? "Today" : new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short" })}<b>{Number(d.slice(8))}</b></span>
              {by.get(d)?.length ? (
                <ul>{by.get(d)!.map((c) => (
                  <li key={c.id} className={`ch ch-${c.channel}`}>
                    <span className="ch-dot" aria-hidden="true" />
                    <Link to={c.emailNumber ? `/marketing/emails/${c.emailNumber}` : `/marketing/calendar?m=${d.slice(0, 7)}`} className="t">{c.title}</Link>
                    <span className={`st ${c.state}`}>{CONTENT_STATE_LABEL[c.state]}</span>
                  </li>))}
                </ul>
              ) : <span className="muted none">Nothing going out</span>}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function Producing({ s, camps, mail }: { s: Awaited<ReturnType<typeof mk.summary>> | null; camps: Awaited<ReturnType<typeof mk.campaigns>> | null; mail: Awaited<ReturnType<typeof em.summary>> | null }) {
  const top = (camps ?? []).filter((c) => c.state !== "cancelled" && (c.leads || c.pipeline || c.won || c.state === "active")).sort((a, b) => (b.pipeline + b.won) - (a.pipeline + a.won) || b.leads - a.leads).slice(0, 4);
  return (
    <section className="factbox mk-made" aria-labelledby="pr-h">
      <div className="mk-side-h"><h2 id="pr-h">What marketing is producing</h2></div>
      {!s ? <p className="empty-note">Loading…</p> : (<>
        <dl>
          <dt><Link to="/marketing/leads?view=all">Leads, last 30 days</Link></dt><dd>{s.leads30}</dd>
          <dt><Link to="/marketing/leads?view=qualified">Qualified, ready to convert</Link></dt><dd>{s.qualified}</dd>
          <dt><Link to="/opportunities">Pipeline from marketing</Link></dt><dd>{usd(s.pipeline)}</dd>
          <dt>Won from marketing</dt><dd>{usd(s.won)}</dd>
          {mail && mail.last30.sent > 0 && <><dt><Link to="/marketing/emails">Email opened, 30 days</Link></dt><dd>{pctOf(mail.last30.openRate)} of {n(mail.last30.sent)}</dd></>}
        </dl>
        {top.length > 0 && (
          <table className="mk-camps">
            <thead><tr><th>Campaign</th><th className="r">Leads</th><th className="r">Pipeline and won</th></tr></thead>
            <tbody>{top.map((c) => (
              <tr key={c.id}><td><Link to={`/marketing/campaigns/${c.number}`}>{c.name}</Link></td><td className="r">{c.leads}{c.goalLeads ? <span className="muted"> of {c.goalLeads}</span> : null}</td><td className="r">{c.pipeline + c.won ? usd(c.pipeline + c.won) : "–"}</td></tr>))}
            </tbody>
          </table>
        )}
      </>)}
    </section>
  );
}
